/**
 * Pipeline invariants — pure planning functions for the In Progress
 * column rules (CLAUDE.md §7.6).
 *
 * Governing principle:
 *
 *   - Paused non-empty ⇒ In Progress must NEVER be empty.
 *   - Paused empty ⇒ In Progress MAY be empty (banner cues drawing
 *     from Submitted).
 *   - The system never auto-promotes from Submitted. Drawing
 *     Submitted work into In Progress is always a deliberate
 *     Assistant action.
 *
 * Rules:
 *
 *   MAX-1: At most one file may have status='in_progress' at any
 *          time. No exceptions.
 *
 *   MANUAL-MOVES-REQUIRE-EMPTY-SLOT:
 *          Manual moves from Submitted → In Progress (draw) and
 *          Paused → In Progress (resume) are allowed only when In
 *          Progress is empty.
 *
 *   PAUSE-FORCES-TAKEOVER:
 *          Pausing the In Progress file always forces an immediate
 *          takeover via the modal. Candidates = (other paused) +
 *          (submitted). If the candidate set is empty the pause is
 *          BLOCKED — the active file stays in In Progress.
 *
 *   SFS-FORCES-TAKEOVER-WHEN-PAUSED:
 *          Send-for-Signature forces the takeover modal only when
 *          Paused is non-empty (paused work must never be stranded).
 *          When Paused is empty, SfS commits and In Progress may
 *          legitimately become empty.
 *
 * These helpers are pure: they take a snapshot of files and return a
 * decision. The actual database mutations live in pipelineService.js,
 * which is the single chokepoint for every status transition.
 */

// Comparators
export const oldestSubmittedFirst = (a, b) =>
  new Date(a.submittedAt || 0) - new Date(b.submittedAt || 0);

export const newestPausedFirst = (a, b) =>
  new Date(b.pausedAt || 0) - new Date(a.pausedAt || 0);

// "Oldest drawn first" — used for the first-load normalizer to keep
// the file that has been in progress the longest and demote the rest.
export const oldestDrawnFirst = (a, b) =>
  new Date(a.drawnAt || 0) - new Date(b.drawnAt || 0);

// Message surfaced when a pause is blocked (PAUSE-FORCES-TAKEOVER).
export const PAUSE_BLOCKED_MESSAGE =
  "There's no other file to take over, so this one stays in progress.";

// Test records (is_test = true, CLAUDE.md §33) are RELEASED from all
// pipeline rules: they never occupy the In Progress slot, never appear
// as takeover candidates, never trigger the warning banner, and are
// never touched by the first-load normalizer. Every planner below
// operates on real files only. Real records keep all rules unchanged.
function realFilesOnly(allFiles) {
  return (allFiles || []).filter((f) => f.isTest !== true);
}

/**
 * Decide what should happen when `file` (currently in_progress) is
 * moved out of In Progress (paused OR sent for signature).
 *
 *   { mode: 'pick', pausedCandidates, submittedCandidates }
 *     — open the PickNextFileModal. The user MUST pick a file. The
 *       outbound + inbound transitions commit atomically when they
 *       select. Cancel reverts the outbound action.
 *
 *   { mode: 'commit' }
 *     — Send-for-Signature only: Paused is empty, so the outbound
 *       action commits immediately and In Progress becomes empty.
 *
 *   { mode: 'blocked', message }
 *     — Pause only: nothing to take over, so the pause is blocked
 *       and the active file stays in In Progress.
 *
 * @param {object} file — the file leaving In Progress
 * @param {Array}  allFiles — full snapshot of files
 * @param {object} opts
 * @param {'pause'|'send_for_signature'} opts.action
 */
export function planNextFileAction(file, allFiles, { action } = {}) {
  // Gate-level test-record bypass (CLAUDE.md §33.4): an outbound action
  // ON a test record is never blocked and never forces a takeover pick —
  // it always commits directly, for every caller and every role. This
  // runs BEFORE any candidate/slot evaluation. (Test records are also
  // filtered out of the candidate sets below, so they never occupy the
  // constrained slot either — the two halves of the invariant.)
  if (file?.isTest === true) return { mode: 'commit' };
  const files = realFilesOnly(allFiles);
  const submittedCandidates = files
    .filter((f) => f.status === 'submitted')
    .sort(oldestSubmittedFirst);

  if (action === 'pause') {
    // Pause: A is about to become paused itself — exclude it from
    // the resumable-paused candidate list. Candidate set is the
    // union of other-paused + submitted (paused never stranded means
    // we must pick *something* if anything exists).
    const pausedOthers = files
      .filter((f) => f.status === 'paused' && f.id !== file?.id)
      .sort(newestPausedFirst);

    if (pausedOthers.length + submittedCandidates.length === 0) {
      return { mode: 'blocked', message: PAUSE_BLOCKED_MESSAGE };
    }
    return {
      mode: 'pick',
      pausedCandidates: pausedOthers,
      submittedCandidates,
    };
  }

  // action === 'send_for_signature'.
  // A is leaving the pipeline — no self-exclusion needed for paused
  // candidates. SfS only forces a takeover when Paused is non-empty;
  // when Paused is empty, In Progress may legitimately go empty.
  const paused = files
    .filter((f) => f.status === 'paused')
    .sort(newestPausedFirst);

  if (paused.length === 0) {
    return { mode: 'commit' };
  }
  return {
    mode: 'pick',
    pausedCandidates: paused,
    submittedCandidates,
  };
}

/**
 * One-time normalizer plan. Brings legacy / first-load state into
 * compliance with the In Progress rules:
 *
 *   1. Multi-active → keep the file with the oldest drawn_at and
 *      demote the rest back to Submitted (original submitted_at
 *      timestamps are preserved).
 *   2. In Progress empty AND Paused non-empty → promote the NEWEST
 *      paused file (honours "Paused never stranded").
 *   3. In Progress empty AND Paused empty → no op (valid state).
 *
 * The system never auto-promotes from Submitted. Drawing Submitted
 * work is always a deliberate Assistant action.
 *
 *   { kind: 'demote',         file }  — in_progress → submitted
 *   { kind: 'promote-paused', file }  — paused      → in_progress
 */
export function planNormalize(allFiles) {
  const files = realFilesOnly(allFiles);
  const ops = [];

  const inProgress = files
    .filter((f) => f.status === 'in_progress')
    .sort(oldestDrawnFirst);

  let keptActive = null;
  if (inProgress.length > 1) {
    keptActive = inProgress[0];
    for (let i = 1; i < inProgress.length; i++) {
      ops.push({ kind: 'demote', file: inProgress[i] });
    }
  } else if (inProgress.length === 1) {
    keptActive = inProgress[0];
  }

  if (!keptActive) {
    const paused = files
      .filter((f) => f.status === 'paused')
      .sort(newestPausedFirst);
    if (paused.length > 0) {
      ops.push({ kind: 'promote-paused', file: paused[0] });
    }
    // Submitted is intentionally NOT auto-promoted — the user must
    // draw it deliberately.
  }

  return ops;
}

/**
 * Tells the In Progress column whether to render the warning banner.
 *
 * Returns true iff: in_progress is empty AND (submitted > 0 OR paused > 0).
 * Derived view of pipeline state — not a flag set by an action.
 *
 * Note: under the live rules, "in_progress empty AND paused > 0" is
 * resolved by the first-load normalizer (promote-paused). The banner
 * still derives the condition correctly for the brief window before
 * normalization fires.
 */
export function shouldShowNoActiveBanner(allFiles) {
  const files = realFilesOnly(allFiles);
  const hasActive = files.some((f) => f.status === 'in_progress');
  if (hasActive) return false;
  return files.some(
    (f) => f.status === 'submitted' || f.status === 'paused'
  );
}

/**
 * Centralised "is the In Progress slot free?" check. Used by Draw /
 * Resume buttons to gate manual promotion to in_progress.
 *
 * Pass the record being promoted as `candidate` where available: a test
 * record (is_test = true) short-circuits to "allowed" before the slot
 * evaluation runs (CLAUDE.md §33.4) — role-agnostic, every caller.
 * Omitting `candidate` preserves the pre-existing slot-only behaviour.
 */
export function canPromoteToInProgress(allFiles, candidate) {
  if (candidate?.isTest === true) return true;
  return !realFilesOnly(allFiles).some((f) => f.status === 'in_progress');
}
